import React, { useEffect } from 'react';
import { Container, Typography, Box, Button, TextField, FormControlLabel, RadioGroup, Radio, FormControl } from '@mui/material';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { getFieldsByPropertyCategory } from '../../../utils/propertyFields';
import { Field } from '../../../types/PropertyCategory';
import PropertyFooterLayout from '../../../components/properties/PropertyFooterLayout';
import { styled } from '@mui/material/styles';

interface PropertyFormContext {
  propertyCategory: string;
}

type PublishOutletContext = {
  handleNavigateToPropertyServices: () => void;
  propertyData: Record<string, any>;
  setPropertyData: (propertyData: Record<string, any>) => void;
  propertyDimension: number;
  setPropertyDimension: (propertyDimension: number) => void;
  setPropertyCategory: (propertyCategory: string) => void;
};

const progress = 80;
const step = 1;

const GreenRadio = styled(Radio)({
  '&.Mui-checked': {
    color: 'primary',
  },
  '& .MuiSvgIcon-root': {
    fontSize: 30,
  },
});

const CustomRadioGroup = styled(RadioGroup)({
  '& .MuiFormControlLabel-root': {
    margin: '0 8px',
  },
});

const CustomTextField = styled(TextField)({
  '& input': {
    fontSize: '1.2rem',
  },
});

const PropertyDataPage: React.FC = () => {
  const { propertyCategory } = useOutletContext<PropertyFormContext>();
  const navigate = useNavigate();
  const { handleNavigateToPropertyServices, setPropertyDimension, setPropertyData, propertyData } = useOutletContext<PublishOutletContext>();

  useEffect(() => {
    // Set propertyDimension if 'Tamaño' exists in propertyData
    if (propertyData['Tamaño'] !== undefined) {
      setPropertyDimension(propertyData['Tamaño']);
    }
  }, [propertyData, setPropertyDimension]);

  const handleFieldChange = (name: string, value: any) => {
    if (name === 'Tamaño') {
      setPropertyDimension(value);
    }
    setPropertyData((prevData: any) => ({ ...prevData, [name]: value }));
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleContinue = () => {
    handleNavigateToPropertyServices();
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const numericValue = Math.max(Number(value), 0); 
    handleFieldChange(name, numericValue);
  };

  const handleBooleanChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value === "Sí" ? true : false;
    handleFieldChange(e.target.name, value ? "Sí" : "No");
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFieldChange(e.target.name, e.target.value);
  };

  const fields: Field[] = getFieldsByPropertyCategory(propertyCategory);


  const isDimensionFilled = propertyData['Tamaño'] !== undefined && propertyData['Tamaño'] !== '';


  return (
    <Container maxWidth="sm" sx={{ display: 'flex',pb:14,pt:2,flexDirection: 'column', alignItems: 'center', width: '100%' }}>
      <Box p={3} sx={{ borderRadius: 2, boxShadow: 3, width: '100%', backgroundColor: 'background.paper' }}>
        <Typography variant="h4" gutterBottom textAlign="center" sx={{ mb: 4 }}>
          Datos de su {propertyCategory}
        </Typography>
        <Box>
          {fields.map(field => (
            <Box key={field.name} sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 2, borderRadius: 1, boxShadow: 1, backgroundColor: 'background.paper' }}>
              <Typography variant="body1" fontSize='1.2rem' sx={{ width: '200px', textAlign: 'left' }}>
                {field.name}
              </Typography>
              <Box sx={{ flexGrow: 1 }}>
                {field.type === 'number' && (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      color='inherit'
                      onClick={() => handleFieldChange(field.name, Math.max((propertyData[field.name] || 0) - 1, 0))}
                      sx={{ 
                        marginRight: 1, 
                        fontSize: '2rem',
                        width: '50px',
                        height: '50px', 
                        borderRadius: '20%',
                        borderColor: 'grey', 
                        color: 'grey',
                        backgroundColor: 'transparent', 
                        '&:hover': {
                          border: '2px solid primary', 
                          color: 'primary', 
                        },
                        '&:active': {
                          borderColor: 'primary', 
                          color: 'primary', 
                        }
                      }}
                    >
                      -
                    </Button>
                    <Typography variant="body1" sx={{ width: '60px', textAlign: 'center', fontSize: '1.3rem' }}>
                      {propertyData[field.name] || 0}
                    </Typography>
                    <Button
                      variant="outlined"
                      color='inherit'
                      onClick={() => handleFieldChange(field.name, (propertyData[field.name] || 0) + 1)}
                      sx={{ 
                        marginLeft: 1, 
                        fontSize: '1.3rem', 
                        width: '50px', 
                        height: '50px', 
                        borderRadius: '20%', 
                        borderColor: 'grey', 
                        color: 'grey', 
                        backgroundColor: 'transparent',
                        '&:hover': {
                          border: '2px solid primary', 
                          color: 'primary', 
                        },
                        '&:active': {
                          borderColor: 'green', 
                          color: 'green', 
                        }
                      }}
                    >
                      +
                    </Button>
                  </Box>
                )}
               {field.type === 'number2' && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                  <TextField
                    variant="outlined"
                    type="number"
                    name={field.name}
                    label={field.name === 'Tamaño' ? 'Metros cuadrados' : field.name}
                    value={propertyData[field.name] || ''}
                    onChange={handleNumberChange}
                    sx={{ 
                      fontSize: '1.2rem',
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: 'grey', 
                        },
                        '&:hover fieldset': {
                          borderColor: 'primary',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'primary',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: 'grey',
                        '&.Mui-focused': {
                          color: 'primary', 
                        },
                      },
                    }}
                  />
                </Box>
              )}
                  {field.type === 'text' && (
                  <CustomTextField
                    variant="outlined"
                    fullWidth
                    name={field.name}
                    value={propertyData[field.name] || ''}
                    onChange={handleTextChange}
                    sx={{ 
                      fontSize: '1.5rem',
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: 'grey', 
                        },
                        '&:hover fieldset': {
                          borderColor: 'primary',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'primary',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: 'grey',
                        '&.Mui-focused': {
                          color: 'primary', 
                        },
                      },
                    }}
                  />
                )}
                {field.type === 'boolean' && (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    <FormControl component="fieldset">
                      <CustomRadioGroup
                        row
                        aria-label={field.name}
                        name={field.name}
                        value={propertyData[field.name] || 'no'}
                        onChange={handleBooleanChange}
                      >
                        <FormControlLabel
                          value="Sí"
                          control={<GreenRadio sx={{ fontSize: '2rem' }} />}
                          label="Sí"
                          sx={{ fontSize: '1.5rem',
                            '& .MuiTypography-root': {
                            fontSize: '1.2rem',
                           }}}
                        />
                        <FormControlLabel
                          value="No"
                          control={<GreenRadio sx={{ fontSize: '2rem' }} />}
                          label="No"
                          sx={{ fontSize: '1.5rem',
                            '& .MuiTypography-root': {
                            fontSize: '1.2rem',
                           }}}
                        />
                      </CustomRadioGroup>
                    </FormControl>
                  </Box>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
      <PropertyFooterLayout
        progress={progress} 
        step={step} 
        onBack={handleBack}
        onContinue={handleContinue}
        isContinueDisabled={!isDimensionFilled}
      />
    </Container>
  );
};

export default PropertyDataPage;
